import { CollapsableStep } from "widgets/booker/shared";
import { useBookerContext, useCartContext } from "widgets/booker/contexts";
import { Flex } from "styled-system/jsx";
import { FormProvider } from "react-hook-form";
import { Button, Combobox, Input, Text } from "@ttc3k/trekker";
import { useCallback, useEffect, useMemo, useState } from "react";
import useCustomerDetailsForm from "./useCustomerDetailsForm";
import { useTranslation } from "react-i18next";
import { ControlledInput } from "shared/components";
import { Shimmer } from "shared/components/atoms";
import { AddressSearchField } from "shared/components/organisms/SurveyQuestionInput";
import { isAddressSearchValueComplete } from "shared/components/organisms/SurveyQuestionInput/addressSearchResponse";
import { createListCollection } from "@ark-ui/react";
import { useOrgScopedUserOperators } from "widgets/booker/shared/hooks/useOrgScopedUserOperators";
import {
  EnumProductSellingConfigRecurringMultiListingMode as MultiListingMode,
  EnumProductType,
} from "gql/generated";
import { getMultilingualString, getResolvedProductSellingConfig } from "shared/utils";
import type { IncludedBusiness } from "widgets/booker/contexts/CartContextProvider/cartContextTypes";
import { MultiBusinessSelector } from "./MultiBusinessSelector";
import { getEarlyRenewalDates } from "widgets/booker/shared/utils";

export const CustomerDetails = () => {
  const { incompleteCustomer, customer, businessField, updateBusinessField, selectedOperatorId, customerAddress, updateCustomerAddress, renewableFeeQuote, customerEligibility, earlyRenewals } =
    useCartContext();
  const { defaultContact, readOnlySelection, booker, userOperators, organizationId, product, membershipIntent, language } =
    useBookerContext();
  const phoneEnabled = booker?.customerFields?.phoneEnabled ?? true;
  const addressRequired = booker?.customerFields?.addressRequired ?? false;
  const { form, onSubmit } = useCustomerDetailsForm(phoneEnabled);

  const showBusinessField =
    (booker?.customerFields?.businessFieldEnabled ?? false) &&
    !(readOnlySelection && selectedOperatorId);

  const { operators: orgScopedOperators, loading: orgScopedOperatorsLoading } =
    useOrgScopedUserOperators(
      userOperators,
      organizationId,
      showBusinessField && !!organizationId,
    );

  const hasUserOperators =
    !orgScopedOperatorsLoading && orgScopedOperators.length > 0;

  const businessFieldDescription = getMultilingualString(
    booker?.customerFields?.businessFieldDescription ?? {},
  ).trim();

  const multiListing =
    product?.type === EnumProductType.RenewableFee
      ? getResolvedProductSellingConfig(product).recurring?.multiListing
      : null;
  const isMembership = product?.type === EnumProductType.RenewableFee;
  const perBusinessAnswersEnabled = !!booker?.perBusinessAnswersEnabled;
  const multiListingMode = isMembership
    ? (multiListing?.mode as MultiListingMode | undefined)
    : perBusinessAnswersEnabled
      ? MultiListingMode.PerBusiness
      : undefined;
  const showMultiBusinessSelector =
    !!multiListingMode &&
    multiListingMode !== MultiListingMode.Single &&
    orgScopedOperators.length > 1;

  const includedBusinesses = useMemo(
    () => businessField.includedBusinesses ?? [],
    [businessField.includedBusinesses],
  );
  const updateIncludedBusinesses = useCallback(
    (next: IncludedBusiness[]) =>
      updateBusinessField({
        operatorEntityId: next[0]?.operatorEntityId,
        businessName: undefined,
        includedBusinesses: next.length > 0 ? next : undefined,
      }),
    [updateBusinessField],
  );
  const quoteLineAmounts = useMemo(() => {
    const lines = (renewableFeeQuote.data?.listingLines ?? []).flatMap(line =>
      line ? [line] : [],
    );
    if (lines.length < 2) return undefined;
    return new Map(lines.map(line => [line.index, line.amount]));
  }, [renewableFeeQuote.data]);

  const isBusinessFieldSatisfied = !showBusinessField ||
    !!businessField.operatorEntityId ||
    !!businessField.businessName;

  const isAddressSatisfied =
    !addressRequired || isAddressSearchValueComplete(customerAddress);

  const hasPrefilledFields =
    !!defaultContact?.firstName ||
    !!defaultContact?.lastName ||
    !!defaultContact?.email ||
    !!defaultContact?.phone ||
    !!defaultContact?.address;

  // Per-field lock: only lock a field that already has a value from the invoice
  const lockedFields = {
    firstName: !!readOnlySelection && !!defaultContact?.firstName,
    lastName: !!readOnlySelection && !!defaultContact?.lastName,
    email: !!readOnlySelection && !!defaultContact?.email,
    phone: !!readOnlySelection && !!defaultContact?.phone,
  };
  const isAddressLocked =
    !!readOnlySelection && isAddressSearchValueComplete(defaultContact?.address);

  // Only skip auto-submit when every required field is already locked
  const isFullyLocked =
    lockedFields.firstName &&
    lockedFields.lastName &&
    lockedFields.email &&
    (lockedFields.phone || !phoneEnabled) &&
    (!addressRequired || isAddressLocked);

  const [showPrefillBanner, setShowPrefillBanner] = useState(hasPrefilledFields);
  const isLockedPrefill = isFullyLocked;

  useEffect(() => {
    form.reset({
      firstName: customer.firstName || "",
      lastName: customer.lastName || "",
      email: customer.email || "",
      phone: customer.phone || "",
    });
  }, [customer.email, customer.firstName, customer.lastName, customer.phone, form]);

  // Auto-submit / revert based on validation state changes.
  // We subscribe via formState proxy reads inside a deferred effect to avoid
  // triggering setState on sibling ControlledInputs during render.
  const { isValid, isValidating } = form.formState;

  useEffect(() => {
    if (isLockedPrefill) return;
    if (!isValidating) return;
    const timer = setTimeout(() => {
      if (form.formState.isValid && !customer.complete && isBusinessFieldSatisfied && isAddressSatisfied) {
        onSubmit();
      } else if (!form.formState.isValid && customer.complete) {
        const email = form.getValues("email");
        const phone = form.getValues("phone");
        const firstName = form.getValues("firstName");
        const lastName = form.getValues("lastName");
        onSubmit();
        incompleteCustomer({ email, phone, firstName, lastName });
      }
    }, 1);
    return () => clearTimeout(timer);
  }, [isValidating, isLockedPrefill, onSubmit, customer.complete, form, incompleteCustomer, isBusinessFieldSatisfied, isAddressSatisfied]);

  // Re-submit when business field or address is filled after form was already
  // valid. Deliberately NOT gated on isLockedPrefill: a locked invoice
  // recipient can still owe a business name or address, and this effect is
  // the only submit path left once the contact inputs are disabled.
  useEffect(() => {
    if (!customer.complete && isValid && isBusinessFieldSatisfied && isAddressSatisfied) {
      const timer = setTimeout(() => onSubmit(), 1);
      return () => clearTimeout(timer);
    }
  }, [isBusinessFieldSatisfied, isAddressSatisfied, customer.complete, isValid, onSubmit]);

  const handlePrefillAccept = useCallback(async () => {
    setShowPrefillBanner(false);
    await onSubmit();
  }, [onSubmit]);

  const handlePrefillClear = useCallback(() => {
    setShowPrefillBanner(false);
    form.reset({ firstName: "", lastName: "", email: "", phone: "" });
    incompleteCustomer({ firstName: "", lastName: "", email: "", phone: "" });
    if (addressRequired) {
      updateCustomerAddress(null);
    }
  }, [form, incompleteCustomer, addressRequired, updateCustomerAddress]);

  const { t } = useTranslation("booker", { keyPrefix: "CUSTOMER_DETAILS" });

  const everyBusinessPaidUp =
    showBusinessField &&
    orgScopedOperators.length > 0 &&
    orgScopedOperators.every(op =>
      earlyRenewals.entries.some(entry => entry.operatorId === op.entityId),
    );
  const earlyRenewalBlock =
    earlyRenewals.blocking[0] ??
    (everyBusinessPaidUp ? earlyRenewals.entries[0] : undefined);
  const isStepComplete =
    customer.complete &&
    isBusinessFieldSatisfied &&
    isAddressSatisfied &&
    !customerEligibility.blocked &&
    !earlyRenewalBlock;
  const blockedMessage =
    getMultilingualString(customerEligibility.blockedMessage ?? {}).trim() ||
    t("NOT_ELIGIBLE");
  const earlyRenewalMessage = (() => {
    if (!earlyRenewalBlock) return null;
    const dates = getEarlyRenewalDates(earlyRenewalBlock, language);
    const business = earlyRenewalBlock.operatorId
      ? orgScopedOperators.find(op => op.entityId === earlyRenewalBlock.operatorId)
          ?.name
      : undefined;
    const notice = business
      ? t("RENEWAL_NOT_OPEN_BUSINESS", { ...dates, business })
      : t("RENEWAL_NOT_OPEN", dates);
    const tierCount = isMembership
      ? getResolvedProductSellingConfig(product).options.length
      : 0;
    return tierCount > 1 ? `${notice}\n${t("RENEWAL_NOT_OPEN_TIER_HINT")}` : notice;
  })();

  // Revert completion if business field or address becomes required but empty
  useEffect(() => {
    if (customer.complete && (!isBusinessFieldSatisfied || !isAddressSatisfied)) {
      incompleteCustomer({
        email: customer.email,
        phone: customer.phone,
        firstName: customer.firstName,
        lastName: customer.lastName,
      });
    }
  }, [customer, isBusinessFieldSatisfied, isAddressSatisfied, incompleteCustomer]);

  const operatorCollection = useMemo(() => {
    const items = orgScopedOperators.map(op => ({
      label: op.name,
      value: op.entityId,
    }));
    return createListCollection({ items });
  }, [orgScopedOperators]);

  useEffect(() => {
    if (
      showBusinessField &&
      hasUserOperators &&
      orgScopedOperators.length === 1 &&
      !businessField.operatorEntityId
    ) {
      updateBusinessField({ operatorEntityId: orgScopedOperators[0].entityId });
    }
  }, [
    showBusinessField,
    hasUserOperators,
    orgScopedOperators,
    businessField.operatorEntityId,
    updateBusinessField,
  ]);

  return (
    <CollapsableStep
      title={t("TITLE")}
      complete={isStepComplete}
      stepId="customer-details"
    >
      <Text color="text.mid">{t("SUBTITLE")}</Text>

      {showPrefillBanner && !customer.complete && !isLockedPrefill && (
        <Flex
          alignItems="center"
          justifyContent="space-between"
          gap="200"
          p="200"
          borderRadius="md"
          bgColor="bg.brand.light"
        >
          <Text visual="smallMedium" color="text.dark">
            {t("PREFILLED")}
          </Text>
          <Flex gap="100" flexShrink={0}>
            <Button size="sm" onClick={handlePrefillAccept}>
              {t("PREFILLED_THANKS")}
            </Button>
            <Button
              size="sm"
              visual="primary"
              mode="light"
              outlineColor="border.dark"
              onClick={handlePrefillClear}
            >
              {t("PREFILLED_CLEAR")}
            </Button>
          </Flex>
        </Flex>
      )}

      <FormProvider {...form}>
        <form>
          <Flex flexDir="column" gap="150">
            <ControlledInput
              name="firstName"
              label={`${t("FORM.FIRST_NAME")}*`}
              disabled={lockedFields.firstName}
            />
            <ControlledInput
              name="lastName"
              label={`${t("FORM.LAST_NAME")}*`}
              disabled={lockedFields.lastName}
            />
            <ControlledInput
              name="email"
              label={`${t("FORM.EMAIL")}*`}
              disabled={lockedFields.email}
            />
            {phoneEnabled && (
              <ControlledInput
                name="phone"
                label={`${t("FORM.PHONE")}*`}
                disabled={lockedFields.phone}
              />
            )}
            {addressRequired && (
              <AddressSearchField
                label={`${t("FORM.ADDRESS")}*`}
                disabled={isAddressLocked}
                value={customerAddress}
                onChange={updateCustomerAddress}
              />
            )}
            {!showBusinessField && includedBusinesses.length > 1 && (
              <Flex flexDir="column" gap="50">
                <Text visual="smallMedium" color="text.dark">
                  {t("FORM.BUSINESSES")}
                </Text>
                {includedBusinesses.map((business, index) => (
                  <Text
                    key={business.operatorEntityId}
                    visual="smallRegular"
                    color="text.mid"
                  >
                    {`${index + 1}. ${business.businessName ?? ""}${
                      index === 0 ? ` (${t("FORM.PRIMARY")})` : ""
                    }`}
                  </Text>
                ))}
              </Flex>
            )}
            {showBusinessField && businessFieldDescription && (
              <Text visual="smallRegular" color="text.mid" whiteSpace="pre-line">
                {businessFieldDescription}
              </Text>
            )}
            {showBusinessField && (
              orgScopedOperatorsLoading ? (
                <Flex
                  flexDir="column"
                  gap="100"
                  aria-busy="true"
                  aria-label={t("FORM.BUSINESS")}
                >
                  <Shimmer h="16px" w="96px" />
                  <Shimmer h="40px" w="full" />
                </Flex>
              ) : hasUserOperators && showMultiBusinessSelector && multiListingMode ? (
                <MultiBusinessSelector
                  operators={orgScopedOperators}
                  organizationId={organizationId}
                  mode={multiListingMode}
                  maxListings={isMembership ? multiListing?.maxListings : undefined}
                  value={includedBusinesses}
                  onChange={updateIncludedBusinesses}
                  lineAmounts={isMembership ? quoteLineAmounts : undefined}
                  initialOperatorIds={membershipIntent?.coveredOperatorIds}
                  associateOnly={!isMembership}
                  allowSelectAll={perBusinessAnswersEnabled}
                  customerEmail={
                    customer?.email?.trim() || defaultContact?.email?.trim() || undefined
                  }
                />
              ) : hasUserOperators ? (
                <Flex flexDir="column" gap="50">
                  <Text visual="smallMedium" color="text.dark">
                    {t("FORM.BUSINESS")}
                  </Text>
                  <Combobox.Root
                    value={
                      businessField.operatorEntityId
                        ? [businessField.operatorEntityId]
                        : []
                    }
                    onValueChange={d =>
                      updateBusinessField({
                        operatorEntityId: d.value[0] ?? undefined,
                        businessName: undefined,
                      })
                    }
                    collection={operatorCollection}
                    placeholder={t("FORM.BUSINESS_PLACEHOLDER")}
                  >
                    {operatorCollection.items.map(item => (
                      <Combobox.Item key={item.value} item={item} {...item} />
                    ))}
                  </Combobox.Root>
                </Flex>
              ) : (
                <Input
                  label={t("FORM.BUSINESS")}
                  placeholder={t("FORM.BUSINESS_PLACEHOLDER")}
                  value={businessField.businessName ?? ""}
                  onChange={e =>
                    updateBusinessField({
                      operatorEntityId: undefined,
                      businessName: e.target.value,
                    })
                  }
                />
              )
            )}
            {customerEligibility.loading && (
              <Text visual="smallRegular" color="text.mid">
                {t("CHECKING_ELIGIBILITY")}
              </Text>
            )}
            {earlyRenewalMessage && (
              <Flex p="200" borderRadius="md" bgColor="bg.warning.lightest">
                <Text visual="smallMedium" color="text.warning.dark" whiteSpace="pre-line">
                  {earlyRenewalMessage}
                </Text>
              </Flex>
            )}
            {customerEligibility.blocked && (
              <Flex p="200" borderRadius="md" bgColor="bg.error.lightest">
                <Text visual="smallMedium" color="text.error.dark" whiteSpace="pre-line">
                  {blockedMessage}
                </Text>
              </Flex>
            )}
          </Flex>
        </form>
      </FormProvider>
    </CollapsableStep>
  );
};
